import { PerspectiveCamera, Vector3 } from 'three';

import { AUTO_ZOOM_PADDING, CAMERA_MIN_DISTANCE } from '../types/bounds-types';
import {
	type BezierControlPoints,
	bezierPointAt,
	bezierTangentAt,
	buildArcControlPoints,
	buildAutoZoomArcPath,
	computePresentationPoint
} from './auto-zoom-arc.cfn';

export interface AutoZoomParams {
	/** Camera source position (30' out) */
	source: Vector3;
	/** Camera target position (look-at point) */
	target: Vector3;
	/** Scene bounding box minimum */
	boundsMin: Vector3;
	/** Scene bounding box maximum */
	boundsMax: Vector3;
	/** The perspective camera (for FOV and aspect) */
	camera: PerspectiveCamera;
	/** Padding factor (default 1.05 = 5%) */
	padding?: number;
	/** Viewport anchor (0-1 normalized). Shifts visible area so look-at point is off-center. */
	viewportAnchor?: { vertical?: number; horizontal?: number };
	/**
	 * Raises the arc as a fraction of its height above the target, so the
	 * resting camera sits that much higher (0.15 = 15% higher). Applied after
	 * the fit, which keeps the framing distance the fit chose.
	 */
	cameraHeightBoost?: number;
}

export interface AutoZoomResult {
	/** Camera position on the straight vector where kitchen fits with padding */
	position: Vector3;
	/** Distance from target to this position */
	distance: number;
	/** Parametric t on the source-to-target segment (0=source, 1=target) */
	t: number;
	/** Quadratic bezier arc path from elevated source to target */
	arcPath: Vector3[];
	/** Bezier control points for exact curve evaluation during animation */
	controlPoints: BezierControlPoints;
	/** Auto-zoom position sampled from the arc at parametric t */
	arcPosition: Vector3;
	/** Tangent direction of the arc at the auto-zoom point */
	arcTangent: Vector3;
	/** Presentation point: 48" from arc auto-zoom position toward camera target */
	presentationPoint: Vector3;
}

interface AsymmetricFrustum {
	tanRight: number;
	tanLeft: number;
	tanUp: number;
	tanDown: number;
}

interface CameraFrame {
	viewDir: Vector3;
	cameraRight: Vector3;
	cameraUp: Vector3;
}

interface SegmentResult {
	position: Vector3;
	distance: number;
	t: number;
}

/**
 * Build a camera coordinate frame from source and target positions.
 */
const buildCameraFrame = (source: Vector3, target: Vector3): CameraFrame => {
	const viewDir = new Vector3()
		.subVectors(target, source)
		.normalize();
	const worldUp = new Vector3(0, 1, 0);
	const cameraRight = new Vector3()
		.crossVectors(viewDir, worldUp)
		.normalize();
	const cameraUp = new Vector3()
		.crossVectors(cameraRight, viewDir)
		.normalize();
	return { viewDir, cameraRight, cameraUp };
};

/**
 * Build asymmetric frustum tangents that account for a viewport anchor offset.
 */
const buildAsymmetricFrustum = (
	camera: PerspectiveCamera,
	viewportAnchor?: { vertical?: number; horizontal?: number }
): AsymmetricFrustum => {
	const fovRadV = (camera.fov * Math.PI) / 180;
	const tanHalfFovV = Math.tan(fovRadV / 2);
	const tanHalfFovH = tanHalfFovV * camera.aspect;

	const vAnchor = Math.max(0.01, Math.min(0.99, viewportAnchor?.vertical ?? 0.5));
	const hAnchor = Math.max(0.01, Math.min(0.99, viewportAnchor?.horizontal ?? 0.5));

	return {
		tanRight: 2 * tanHalfFovH * (1 - hAnchor),
		tanLeft: 2 * tanHalfFovH * hAnchor,
		tanUp: 2 * tanHalfFovV * (1 - vAnchor),
		tanDown: 2 * tanHalfFovV * vAnchor
	};
};

/**
 * Build the 8 bounding-box corners (centered on origin) and the offset
 * from the camera look-at target to the actual bounds center.
 */
const buildBoundsCorners = (params: {
	boundsMin: Vector3;
	boundsMax: Vector3;
	target: Vector3;
	padding: number;
}): { corners: Vector3[]; centerOffset: Vector3 } => {
	const boundsSize = new Vector3().subVectors(params.boundsMax, params.boundsMin);
	const hw = (boundsSize.x * params.padding) / 2;
	const hh = (boundsSize.y * params.padding) / 2;
	const hd = (boundsSize.z * params.padding) / 2;

	const corners = [
		new Vector3(-hw, -hh, -hd),
		new Vector3(hw, -hh, -hd),
		new Vector3(-hw, hh, -hd),
		new Vector3(hw, hh, -hd),
		new Vector3(-hw, -hh, hd),
		new Vector3(hw, -hh, hd),
		new Vector3(-hw, hh, hd),
		new Vector3(hw, hh, hd)
	];

	const boundsCenter = new Vector3()
		.addVectors(params.boundsMin, params.boundsMax)
		.multiplyScalar(0.5);
	const centerOffset = new Vector3().subVectors(boundsCenter, params.target);

	return { corners, centerOffset };
};

/**
 * Compute the minimum camera-to-target distance so that every bounding-box
 * corner fits inside the (possibly asymmetric) frustum.
 */
const computeFitDistance = (params: {
	corners: Vector3[];
	centerOffset: Vector3;
	frame: CameraFrame;
}): ((frustum: AsymmetricFrustum) => number) => (frustum) => {
	let fitDistance = 0;

	for (const corner of params.corners) {
		const pos = corner.clone().add(params.centerOffset);
		const r = pos.dot(params.frame.cameraRight);
		const u = pos.dot(params.frame.cameraUp);
		const depth = pos.dot(params.frame.viewDir);

		const distForH = r >= 0
			? (r / frustum.tanRight) - depth
			: ((-r) / frustum.tanLeft) - depth;

		const distForV = u >= 0
			? (u / frustum.tanUp) - depth
			: ((-u) / frustum.tanDown) - depth;

		fitDistance = Math.max(fitDistance, distForH, distForV);
	}

	return fitDistance;
};

/**
 * Clamp a fit-distance onto the source→target segment.
 */
const clampToSegment = (params: {
	source: Vector3;
	target: Vector3;
	fitDistance: number;
}): SegmentResult => {
	const totalLen = params.source.distanceTo(params.target);
	const awayDir = new Vector3()
		.subVectors(params.source, params.target)
		.normalize();
	const autoZoomPos = params.target.clone()
		.add(awayDir.multiplyScalar(params.fitDistance));

	const segmentVec = new Vector3().subVectors(params.target, params.source);
	const toPos = new Vector3().subVectors(autoZoomPos, params.source);
	const minT = CAMERA_MIN_DISTANCE / totalLen;
	const t = Math.max(0, Math.min(1 - minT, toPos.dot(segmentVec) / segmentVec.lengthSq()));

	const position = new Vector3().lerpVectors(params.source, params.target, t);
	return { position, distance: position.distanceTo(params.target), t };
};

/**
 * Calculate the auto-zoom camera position along the source-to-target vector,
 * then map it onto a quadratic bezier arc path.
 *
 * The parametric t from the frustum-fitting is used to sample the arc path
 * for the actual camera position, tangent, and presentation point.
 */
export const calculateAutoZoomPosition = (params: AutoZoomParams): AutoZoomResult => {
	const { source, target, boundsMax, camera, viewportAnchor } = params;
	const padding = params.padding ?? AUTO_ZOOM_PADDING;

	const frame = buildCameraFrame(source, target);
	const { corners, centerOffset } = buildBoundsCorners({
		boundsMin: params.boundsMin, boundsMax, target, padding
	});
	const frustum = buildAsymmetricFrustum(camera, viewportAnchor);
	const fitDistance = computeFitDistance({ corners, centerOffset, frame })(frustum);
	const segmentResult = clampToSegment({ source, target, fitDistance });

	// Build the arc and sample at the fit t value
	// Source is already at the elevated arc start position
	const cp = buildArcControlPoints({ source, target });

	// Lift the arc about the look-at point. The curve ends at the target, whose
	// height above itself is zero, so scaling the other two control points about
	// target.y raises every other point — including the resting sample — by
	// exactly this fraction. Done after the fit so t (and with it the framing
	// distance) is the one the fit chose: boosting the source instead would
	// steepen the view and slide the camera further down the arc, ending lower
	// than it started.
	const boost = params.cameraHeightBoost ?? 0;
	if (boost !== 0) {
		cp.arcStart.y = target.y + ((cp.arcStart.y - target.y) * (1 + boost));
		cp.control.y = target.y + ((cp.control.y - target.y) * (1 + boost));
	}

	const arcPath = buildAutoZoomArcPath(cp);
	const arcPosition = bezierPointAt(cp, segmentResult.t);
	const arcTangent = bezierTangentAt(cp, segmentResult.t);
	const presentationPoint = computePresentationPoint(arcPosition, target);

	return {
		...segmentResult,
		arcPath,
		controlPoints: cp,
		arcPosition,
		arcTangent,
		presentationPoint
	};
};
